import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import iconLoadingIA from '../imagens/ialoading.gif';
import { useAssinatura } from '../hooks/useAssinatura';
import { abrirModalPlanos } from './precos';

// Remoção de fundo é gratuita, mas exige que o usuário tenha créditos > 0
// na conta (apenas como verificação de conta ativa).

/**
 * Modal padronizado para remoção de fundo.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - source: File | string (URL) — imagem a ser processada
 *  - onConfirm: (resultado: string | File) => void
 *      recebe a URL/dataURL processada OU a source original (se Salvar sem remover)
 *  - userId: number — usuário logado (para débito de créditos)
 *  - creditos: number — saldo atual
 *  - onCreditosUpdate: (novoSaldo: number) => void — atualiza saldo no parent
 */
export default function RemoveBackgroundModal({
  open,
  onClose,
  source,
  onConfirm,
  userId,
  creditos = 0,
  onCreditosUpdate,
}) {
  const [processando, setProcessando] = useState(false);
  const [processado, setProcessado] = useState(null);
  const [erro, setErro] = useState(null);
  const [previewOriginal, setPreviewOriginal] = useState(null);
  const [semCreditos, setSemCreditos] = useState(false);
  // Assinantes (incluindo origem=crieoferta) podem usar mesmo com 0 créditos.
  const { ativa: assinaturaAtiva } = useAssinatura();

  useEffect(() => {
    if (!open) {
      setProcessado(null);
      setErro(null);
      setProcessando(false);
      setPreviewOriginal(null);
      setSemCreditos(false);
      return;
    }
    if (!source) return;
    if (typeof source === 'string') {
      setPreviewOriginal(source);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewOriginal(e.target.result);
      reader.readAsDataURL(source);
    }
  }, [open, source]);

  async function chamarRemoveBg() {
    const ENDPOINT = 'https://crieoferta.com/rmbg.php';
    let res;
    if (typeof source === 'string') {
      res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: 'imageUrl=' + encodeURIComponent(source),
      });
    } else {
      const fd = new FormData();
      fd.append('image', source);
      res = await fetch(ENDPOINT, {
        method: 'POST',
        body: fd,
      });
    }
    if (!res.ok) throw new Error(`Erro de rede (HTTP ${res.status})`);
    const data = await res.json();
    if (data?.status === 'ok' && data?.img) return data.img;
    if (data?.img) return data.img;
    throw new Error(data?.message || 'Falha ao remover fundo.');
  }

  const handleRemoverFundo = async () => {
    if (processando) return;
    setErro(null);
    setSemCreditos(false);

    // Remover fundo é gratuito, mas o usuário precisa ter ao menos 1 crédito
    // disponível para usar o recurso (proteção contra contas inativas/zeradas).
    // Assinantes (origem=crieoferta, plano ativo, admin) ignoram esse limite.
    if (Number(creditos) <= 0 && !assinaturaAtiva) {
      setSemCreditos(true);
      return;
    }

    setProcessando(true);
    try {
      // Não consome créditos — apenas executa a remoção de fundo.
      const resultado = await chamarRemoveBg();
      setProcessado(resultado);
    } catch (e) {
      setErro(e.message || 'Falha ao remover fundo.');
    } finally {
      setProcessando(false);
    }
  };

  const handleSalvar = async () => {
    if (processando) return;
    if (processado) {
      onConfirm && (await onConfirm(processado));
      onClose && onClose();
      return;
    }
    onConfirm && (await onConfirm(source));
    onClose && onClose();
  };

  const abrirPlanos = () => {
    onClose && onClose();
    // Pequeno atraso para o modal de remoção de fundo desmontar antes
    setTimeout(() => {
      try { abrirModalPlanos(); } catch {}
    }, 50);
  };

  if (!open) return null;

  const previewAtual = processado || previewOriginal;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => !processando && onClose && onClose()}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15,10,21,0.65)',
          zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 24 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: 620, background: '#fff',
            borderRadius: 20, boxShadow: '0 18px 60px rgba(0,0,0,0.35)',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
          }}
        >
          {/* Header roxo */}
          <div style={{
            padding: '14px 20px',
            background: 'linear-gradient(135deg, #3C005A 0%, #73207C 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            color: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 18 }} />
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                Remover Fundo
              </h3>
            </div>
            <button
              type="button"
              onClick={() => !processando && onClose && onClose()}
              disabled={processando}
              style={{
                background: 'transparent', border: 'none', fontSize: 18,
                color: '#fff', cursor: processando ? 'not-allowed' : 'pointer',
                opacity: 0.85,
              }}
              aria-label="Fechar"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Banner info */}
            <div style={{
              background: '#F5EDFA', border: '1px solid #E8E1EF',
              borderRadius: 12, padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
              color: '#3C005A', fontSize: 14,
            }}>
              <i className="fa-solid fa-wand-magic-sparkles" style={{ color: '#73207C' }} />
              <span>Remova o fundo das suas imagens com nossa ferramenta inteligente!</span>
            </div>

            {/* Preview + Botões */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr auto', gap: 16,
              alignItems: 'stretch',
            }}>
              <div style={{
                minHeight: 200, borderRadius: 12,
                border: '1px solid #E8E1EF',
                background: processado
                  ? 'repeating-conic-gradient(#f0eaf5 0% 25%, #fff 0% 50%) 50% / 20px 20px'
                  : '#F7F5F8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative', overflow: 'hidden',
              }}>
                {processando && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(255,255,255,0.8)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: 8, zIndex: 2,
                  }}>
                    <img src={iconLoadingIA} width="70" alt="Processando" />
                    <span style={{ color: '#5C4A63', fontSize: 13 }}>
                      Removendo fundo...
                    </span>
                  </div>
                )}
                {previewAtual && (
                  <img
                    src={previewAtual}
                    alt="Imagem"
                    style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain' }}
                  />
                )}
              </div>

              <div style={{
                display: 'flex', flexDirection: 'column', gap: 10,
                justifyContent: 'center', minWidth: 180,
              }}>
                <button
                  type="button"
                  onClick={handleRemoverFundo}
                  disabled={processando || !!processado}
                  style={{
                    background: processado ? '#B89BC4' : '#9B6FB8',
                    color: '#fff', border: 'none', borderRadius: 10,
                    padding: '12px 18px',
                    cursor: processando || processado ? 'not-allowed' : 'pointer',
                    fontWeight: 700, fontSize: 14,
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(115,32,124,0.25)',
                  }}
                >
                  <i className="fa-solid fa-wand-magic-sparkles" />
                  Remover Fundo
                </button>
                <button
                  type="button"
                  onClick={handleSalvar}
                  disabled={processando}
                  style={{
                    background: '#10B981',
                    color: '#fff', border: 'none', borderRadius: 10,
                    padding: '12px 18px',
                    cursor: processando ? 'not-allowed' : 'pointer',
                    fontWeight: 700, fontSize: 14,
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(16,185,129,0.25)',
                  }}
                >
                  <i className="fa-solid fa-check" />
                  Salvar
                </button>
                <span style={{
                  fontSize: 11, color: '#10B981', textAlign: 'center', marginTop: 2, fontWeight: 700,
                }}>
                  Gratuito
                </span>
              </div>
            </div>

            {semCreditos && (
              <div style={{
                background: '#FEF3C7', border: '1px solid #FCD34D',
                borderRadius: 12, padding: '12px 14px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#92400E', fontSize: 14 }}>
                  <i className="fa-solid fa-triangle-exclamation" />
                  <span>
                    Você precisa ter créditos disponíveis na conta para usar este recurso.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={abrirPlanos}
                  style={{
                    background: '#73207C', color: '#fff', border: 'none',
                    borderRadius: 10, padding: '8px 14px', cursor: 'pointer',
                    fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap',
                  }}
                >
                  Comprar créditos
                </button>
              </div>
            )}

            {erro && (
              <div style={{
                background: '#FEE2E2', border: '1px solid #FCA5A5',
                borderRadius: 12, padding: '10px 14px',
                color: '#991B1B', fontSize: 14,
              }}>
                {erro}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
